import React from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { FormWrapper } from 'uiComponents/form/form'
import TextInputFieldFormik from 'uiComponents/input/textInput/textInputFieldFormik'
import { BaseRouteParams } from 'hocs'
import ActionButton, { ActionButtonA } from 'uiComponents/buttons'
import { useVenueLocations } from '../../../../tapPlacements/studio/helpers'
import SingleSelectFieldFormik from 'uiComponents/input/singleSelect/singleSelectFieldFormik'
import { useParkMapViewerContext } from '../../context/mapViewer'
import useOnPageLeave from '../../hooks/useOnPageLeave'
import useParkMap from '../../hooks/useParkMap'
import { Coord, Parkmap } from '../../models'
import InformationBox from '../informationBox'
import LocationAutocomplete from './autocomplete'
import * as yup from 'yup'
import ToggleMapEnabled from './toggleMapEnabled'
import { useTranslation } from 'translations'

const Conatiner = styled.div`
    flex: 1;
    display: flex;
    & > form {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding-bottom: 1em;
        padding-top: 1em;
    }
`

export const Field = styled.div`
    margin-bottom: 2em;
`

export const FormContent = styled.div`
    position: relative;
    flex-grow: 1;
`

export const FormFooter = styled.div`
    border-top-width: 1px;
    border-top-style: solid;
    border-top-color: ${(props) => props.theme.colors.border};
    padding-top: 1em;
    display: flex;
    justify-content: space-between;
`

const Info = styled.div`
    margin: 0.5em 0 1.5em 0;
`

interface MapFormProps {
    data?: Parkmap | null
    onSubmit: (values: Parkmap) => void
}

const mapForm = ({ data, onSubmit }: MapFormProps) => {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const venueLocations = useVenueLocations(accountSlug)
    const { isRegionChanged, setIsRegionChanged, zoom, coords } = useParkMapViewerContext()
    const { getBounds } = useParkMap()

    const mapValidationSchema = yup.object().shape({
        name: yup
            .string()
            .min(3, t('venue.parkMap.mapDetails.validation.tooShort'))
            .max(50, t('venue.parkMap.mapDetails.validation.tooLong'))
            .required(t('common.validation.required')),
        locationId: venueLocations.length > 1 ? yup.string().required(t('common.validation.required')) : yup.string(),
    })

    useOnPageLeave()

    return (
        <Conatiner>
            {data && (
                <FormWrapper
                    enableReinitialize
                    initialValues={data}
                    onSubmit={(values) => {
                        const bounds = getBounds()

                        onSubmit({
                            ...values,
                            region: {
                                zoom: zoom,
                                center: coords as Coord,
                                north: bounds?.getNorthEast().lat() || 0,
                                east: bounds?.getNorthEast().lng() || 0,
                                south: bounds?.getSouthWest().lat() || 0,
                                west: bounds?.getSouthWest().lng() || 0,
                            },
                        })
                    }}
                    validationSchema={mapValidationSchema}
                >
                    <FormContent>
                        <Info>
                            <InformationBox text={t('venue.parkMap.mapDetails.regionInfo')} />
                        </Info>
                        <Field>
                            <ToggleMapEnabled onChange={() => setIsRegionChanged(true)} />
                        </Field>
                        <Field>
                            <TextInputFieldFormik
                                id="name"
                                name="name"
                                label={t('venue.parkMap.mapDetails.nameLabel')}
                                placeholder={t('venue.parkMap.mapDetails.namePlaceholder')}
                                onMouseDown={() => setIsRegionChanged(true)}
                            />
                        </Field>
                        <Field>
                            {venueLocations.length > 1 && (
                                <SingleSelectFieldFormik
                                    name="locationId"
                                    options={venueLocations}
                                    noSelectOption={t('venue.parkMap.mapDetails.locationPlaceholder')}
                                    label={t('venue.parkMap.mapDetails.locationLabel')}
                                />
                            )}
                        </Field>
                        <Field>
                            <LocationAutocomplete mapId={data.id} />
                        </Field>
                    </FormContent>
                    <FormFooter>
                        <ActionButtonA secondary size="large" href={`/account/${accountSlug}/venue/park_map/list`}>
                            {t('venue.parkMap.mapDetails.backToList')}
                        </ActionButtonA>
                        <ActionButton size="large" type="submit">
                            {isRegionChanged ? t('venue.parkMap.mapDetails.saveChanges') : t('common.save')}
                        </ActionButton>
                    </FormFooter>
                </FormWrapper>
            )}
        </Conatiner>
    )
}

export default mapForm
